/**
 * Mirror of API payloadCrypto — encrypt/decrypt auth wire envelopes.
 * Key = SHA-256(accessToken + VITE_CRM_PAYLOAD_PEPPER).
 */

function getPepper(): string {
  return String(import.meta.env.VITE_CRM_PAYLOAD_PEPPER || '').trim()
}

async function sha256Bytes(message: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(message)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return new Uint8Array(hash)
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.trim()
  const out = new Uint8Array(clean.length / 2)
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16)
  }
  return out
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export async function encryptJson(value: unknown, accessToken: string): Promise<string> {
  const token = String(accessToken || '').trim()
  if (!token) throw new Error('accessToken required for payload crypto')

  const keyBytes = await sha256Bytes(`${token}|${getPepper()}`)
  const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['encrypt'])
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const plain = new TextEncoder().encode(JSON.stringify(value ?? {}))
  const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain)
  const cipherBytes = new Uint8Array(cipherBuf)
  // WebCrypto appends 16-byte auth tag at the end
  const data = cipherBytes.slice(0, cipherBytes.length - 16)
  const tag = cipherBytes.slice(cipherBytes.length - 16)
  return `${bytesToHex(iv)}:${bytesToHex(tag)}:${bytesToHex(data)}`
}

export async function decryptJson<T = any>(payload: string, accessToken: string): Promise<T> {
  const raw = String(payload || '').trim()
  const token = String(accessToken || '').trim()
  if (!raw || !token) return {} as T

  const [ivHex, tagHex, dataHex] = raw.split(':')
  if (!ivHex || !tagHex || !dataHex) {
    throw new Error('Invalid encrypted payload format')
  }

  const keyBytes = await sha256Bytes(`${token}|${getPepper()}`)
  const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt'])

  const iv = hexToBytes(ivHex)
  const tag = hexToBytes(tagHex)
  const data = hexToBytes(dataHex)
  const combined = new Uint8Array(data.length + tag.length)
  combined.set(data, 0)
  combined.set(tag, data.length)

  const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, combined)
  const parsed = JSON.parse(new TextDecoder().decode(plainBuf))
  return parsed as T
}

export async function sealEnvelope(data: unknown, accessToken: string): Promise<{ enc: string }> {
  return { enc: await encryptJson(data, accessToken) }
}

export async function openEnvelope<T = any>(
  body: any,
  accessToken: string | null | undefined
): Promise<T> {
  if (body && typeof body === 'object' && typeof body.enc === 'string' && body.enc && accessToken) {
    return decryptJson<T>(body.enc, String(accessToken))
  }
  return (body && typeof body === 'object' ? body : {}) as T
}

/** Resolve permissions from user payload (encrypted field or legacy plaintext). */
export async function unsealUserPermissions(
  user: any,
  accessToken: string | null | undefined
): Promise<Record<string, boolean>> {
  if (!user || typeof user !== 'object') return {}

  if (user.permissionsEnc && accessToken) {
    try {
      return await decryptJson<Record<string, boolean>>(String(user.permissionsEnc), String(accessToken))
    } catch (e) {
      console.error('[payloadCrypto] decrypt failed:', e)
      return {}
    }
  }

  if (user.permissions && typeof user.permissions === 'object') {
    return user.permissions as Record<string, boolean>
  }

  return {}
}
